import { Dialog, Transition } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import MobileNumberWithScriptCopy from "@/app/components/ui/MobileNumberWithScriptCopy";
import { IWhatsappTemplatePreviewModalProps } from "@/app/components/ui/interface";


interface IPreviewFormFields {
  mobile: string;
}

const WhatsappTemplatePreviewModal: React.FC<IWhatsappTemplatePreviewModalProps> = ({
  open,
  onClose,
  title = "WhatsApp Message Preview",
  initialMobile = "",
  script,
  fullAddress,
  nearbyLandmarks,
  isSending = false,
  sendButtonLabel = "Send WhatsApp",
  sendingLabel = "Sending...",
  onSend,
  onCancel,
  onCopyScript,
  onMobileChange,
  reasonOptions,
  reasonLabel = "Reason",
  reasonPlaceholder = "Select reason",
  onReasonChange,
}) => {
  const reasonRequired = reasonOptions !== undefined;
  const [selectedReasonId, setSelectedReasonId] = useState<string>("");
  const [reasonError, setReasonError] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedReasonId("");
      setReasonError(false);
      setIsOpen(false);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const formHook = useForm<IPreviewFormFields>({
    mode: "onChange",
    defaultValues: { mobile: initialMobile },
  });

  useEffect(() => {
    if (open) {
      formHook.reset({ mobile: initialMobile });
    }
  }, [open, initialMobile]);

  const mobileValue = formHook.watch("mobile");
  const lastLoggedMobileRef = useRef<string>(initialMobile);

  useEffect(() => {
    if (!open) return;
    if (mobileValue === lastLoggedMobileRef.current) return;
    lastLoggedMobileRef.current = mobileValue;
    onMobileChange?.(mobileValue);
  }, [mobileValue, open, onMobileChange]);

  useEffect(() => {
    if (open) lastLoggedMobileRef.current = initialMobile;
  }, [open, initialMobile]);

  const handleSend = formHook.handleSubmit((data) => {
    if (reasonRequired && !selectedReasonId) {
      setReasonError(true);
      return;
    }
    onSend(data.mobile, selectedReasonId || undefined);
  });

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };
  if (reasonOptions !== undefined) console.log(reasonOptions, "reasonOptions");
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="z-[60] fixed inset-0 overflow-y-auto"
        onClose={() => {
          if (isSending) return;
          // onClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="sm:block flex justify-center items-end px-4 sm:p-0 pt-4 pb-20 min-h-screen text-center">
          <span className="sm:inline-block hidden sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="inline-block align-bottom bg-white shadow-xl sm:my-8 rounded-2xl sm:w-full sm:max-w-[40rem] text-left transform transition-all overflow-visible sm:align-middle px-4 sm:p-6 pt-5 pb-4 sm:pb-4">
              <Dialog.Title as="h3" className="mb-1 font-medium text-[#000] text-sm">
                {title}
              </Dialog.Title>
              <hr className="mt-3" />
              <div>
                {(fullAddress || nearbyLandmarks) && (
                  <div className="mt-4 rounded-lg bg-[#F9FAFB] border border-gray-100 p-3 space-y-3">
                    {fullAddress && (
                      <div>
                        <p className="text-[12px] font-medium text-[#727272] mb-0.5">Full Address</p>
                        <p className="text-[12px] font-semibold text-[#101828] whitespace-pre-line">{fullAddress}</p>
                      </div>
                    )}
                    {nearbyLandmarks && (
                      <div>
                        <p className="text-[12px] font-medium text-[#727272] mb-0.5">Nearby Landmarks</p>
                        <p className="text-[12px] font-semibold text-[#101828] whitespace-pre-line">{nearbyLandmarks}</p>
                      </div>
                    )}
                  </div>
                )}
                {reasonRequired && (
                  <div className="mt-4">
                    <label className="text-[12px] font-medium text-[#727272] block mb-1">
                      {reasonLabel} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        disabled={(reasonOptions?.length ?? 0) === 0}
                        className={`relative w-full cursor-default rounded-md border bg-white py-2.5 pl-3 pr-10 text-left text-[12px] font-semibold shadow-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-150 ${
                          reasonError
                            ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500 bg-red-50"
                            : "border-gray-300 text-gray-900 focus:ring-indigo-600 focus:border-indigo-600 bg-white"
                        }`}
                      >
                        <span className="block truncate">
                          {reasonOptions?.find((r) => r.id === selectedReasonId)?.title ||
                            ((reasonOptions?.length ?? 0) === 0
                              ? "Loading reasons..."
                              : reasonPlaceholder)}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />
                        </span>
                      </button>

                      {isOpen && (reasonOptions?.length ?? 0) > 0 && (
                        <div className="absolute z-[100] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {(reasonOptions ?? []).map((r) => (
                            <div
                              key={r.id}
                              onClick={() => {
                                setSelectedReasonId(r.id);
                                setReasonError(false);
                                onReasonChange?.(r.id);
                                setIsOpen(false);
                              }}
                              className={`cursor-pointer select-none py-2 px-3 text-left hover:bg-gray-100 hover:text-black transition-colors duration-150 ${
                                selectedReasonId === r.id
                                  ? "bg-gray-100 text-black font-semibold"
                                  : "text-gray-900 font-normal"
                              }`}
                            >
                              {r.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {reasonError && (
                      <p className="text-[11px] text-red-500 mt-1">{reasonLabel} is required</p>
                    )}
                  </div>
                )}
                <div className="mt-4">
                  <MobileNumberWithScriptCopy
                    register={formHook.register("mobile", {
                      required: true,
                      pattern: /^[0-9]{10}$/,
                      minLength: 10,
                      maxLength: 10,
                    })}
                    fieldError={formHook.formState.errors.mobile}
                    mobileValue={mobileValue}
                    script={script}
                    disabled={isSending}
                    onCopyText={onCopyScript}
                  />
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSending}
                    className="px-4 py-2 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={isSending}
                    className="inline-flex items-center gap-2 justify-center bg-[#07863d] px-6 py-2 rounded-md w-full sm:w-auto font-semibold text-white text-xs disabled:opacity-50"
                  >

                    {isSending ? sendingLabel : sendButtonLabel}
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default WhatsappTemplatePreviewModal;
